/**
 * BuyerSupplierBoundary (AJIP-16.02.008 · WS19)
 * ---------------------------------------------------------------------
 * Founder Ruling S5 — Buyer and Supplier Journey instances remain
 * COMPLETELY SEPARATE. Communication is limited to validated references
 * only. Supplier code MUST NOT read Buyer Journey context, load Buyer
 * state, or infer Buyer decisions.
 *
 * This module is:
 *   - Validation-only.
 *   - Provider-independent.
 *   - Free of external-system imports.
 *   - Free of storage access.
 *   - Free of customer-facing behaviour.
 *   - Without authority over eligibility, activation, progression or
 *     completion.
 *
 * The only permitted cross-Journey signal is a caller-supplied
 * `buyerReferenceRef` — an opaque token whose meaning is external.
 * This module validates its shape only.
 */
import { BuyerSupplierBoundaryError } from './SupplierJourneyErrors';

const MAX_REF_LEN = 128;
const REF_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:@\-/]{0,127}$/;

const isNonEmptyString = (v) => typeof v === 'string' && v.trim().length > 0;

const validateBuyerReferenceRef = (buyerReferenceRef) => {
  if (!isNonEmptyString(buyerReferenceRef)) {
    throw new BuyerSupplierBoundaryError('buyerReferenceRef must be non-empty string');
  }
  if (buyerReferenceRef.length > MAX_REF_LEN) {
    throw new BuyerSupplierBoundaryError('buyerReferenceRef exceeds max length');
  }
  if (!REF_PATTERN.test(buyerReferenceRef)) {
    throw new BuyerSupplierBoundaryError('buyerReferenceRef has invalid shape');
  }
  return true;
};

/**
 * Attach an opaque Buyer reference into supplier context. Never resolves
 * that reference, never reads Buyer state. Caller must pre-authorise.
 */
const attachBuyerReference = ({ supplierContext, buyerReferenceRef }) => {
  validateBuyerReferenceRef(buyerReferenceRef);
  return Object.freeze({ ...(supplierContext || {}), buyerReferenceRef });
};

/**
 * Governance reminder — this boundary intentionally has no `resolve`,
 * `fetch`, `load`, or `read` verb. Suppliers see only references.
 */
const BOUNDARY_GOVERNANCE = Object.freeze({
  supplier_may: 'record-opaque-reference',
  supplier_shall_never: 'resolve|load|read-buyer-context|infer-buyer-decision',
});

const BOUNDARY_NAME = 'BuyerSupplierBoundary';

export {
  BOUNDARY_NAME,
  BOUNDARY_GOVERNANCE,
  validateBuyerReferenceRef,
  attachBuyerReference,
};
